import type { PersonRole, ScenarioPerson, ScenarioRole } from './types'

/** Optional seats editable on a person card (Owner is separate — see Owner checkbox). */
export const CARD_EDIT_ROLES: PersonRole[] = ['editor', 'trainer', 'participant']
/** Roles that can be bulk-granted / revoked on a multi-select (never Owner). */
export const BULK_PERSON_ROLES: PersonRole[] = ['editor', 'trainer', 'participant']
/** Privilege order for multi-role display (Owner first → Participant last). */
export const PERSON_ROLE_RANK: readonly PersonRole[] = ['owner', 'trainer', 'editor', 'participant']

/** Role override picks — Scenarios feature roles (Admin ≠ platform ABP admin). */
export const DEMO_ROLES: ScenarioRole[] = [
  'admin',
  'owner',
  'editor',
  'trainer',
  'participant',
  'easter_eggs',
]
export const ROLE_LABEL: Record<ScenarioRole, string> = {
  admin: 'Admin',
  owner: 'Owner',
  editor: 'Editor',
  trainer: 'Trainer',
  participant: 'Participant',
  easter_eggs: 'Easter Eggs',
}

/** Static Settings previews (not live clock / GPS). */
export const TIME_FORMAT_EXAMPLE_HM = '10:50 AM'
export const TIME_FORMAT_EXAMPLE_HMS = '10:50:03 AM'
/** ~369 ft — matches Find-panel sample distances. */
export const DISPLAY_UNITS_EXAMPLE_M = 369 / 3.28084

export function personIsActive(person: ScenarioPerson): boolean {
  return !person.isDeleted
}

const STUDIO_USER_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** ABP user id when `userId` is a Guid. An email or other string is not an account id. */
export function studioAccountId(value: string | null | undefined): string {
  const text = value?.trim() ?? ''
  return STUDIO_USER_ID.test(text) ? text : ''
}

/** ABP user Guid on the roster row, or empty when the person has no account yet. */
export function personUserId(person: Pick<ScenarioPerson, 'userId'>): string {
  return studioAccountId(person.userId)
}

export function findPersonByUserId(
  people: readonly ScenarioPerson[],
  userId: string | null | undefined,
): ScenarioPerson | undefined {
  const key = studioAccountId(userId).toLowerCase()
  if (!key) return undefined
  return people.find((p) => personUserId(p).toLowerCase() === key)
}

/** Roster row, account id, or email. For display of a stored key, including rows removed from the roster. */
export function findPersonByKey(
  people: readonly ScenarioPerson[],
  key: string | null | undefined,
): ScenarioPerson | undefined {
  const text = key?.trim().toLowerCase()
  if (!text) return undefined
  return people.find((person) => {
    if (person.id.toLowerCase() === text) return true
    if (personUserId(person).toLowerCase() === text) return true
    return person.email.trim().toLowerCase() === text
  })
}

const PERSON_ROLE_SET = new Set<string>(['owner', 'editor', 'trainer', 'participant'])

function readWireString(raw: unknown): string {
  if (typeof raw === 'string') return raw.trim()
  if (typeof raw === 'number' || typeof raw === 'bigint') return String(raw).trim()
  return ''
}

function readWireRoles(raw: Record<string, unknown>): PersonRole[] {
  const src = raw.roles ?? raw.Roles
  if (!Array.isArray(src)) return ['participant']
  const roles = src
    .map((r) => (typeof r === 'string' ? r.trim().toLowerCase() : ''))
    .filter((r): r is PersonRole => PERSON_ROLE_SET.has(r))
  return roles.length ? roles : ['participant']
}

/**
 * Parse a PeopleJson row. `id` is the roster row. `userId` is kept only when it is an ABP Guid.
 * A legacy row whose only key is an email keeps that string as `id` until the repair command
 * assigns a roster Guid. This read does not mint a new id.
 */
export function normalizePerson(raw: unknown): ScenarioPerson | null {
  if (!raw || typeof raw !== 'object') return null
  const p = raw as Record<string, unknown>
  const email = readWireString(p.email ?? p.Email)
  const fromId = readWireString(p.id ?? p.Id)
  const fromUserId = readWireString(p.userId ?? p.UserId)
  const accountId = studioAccountId(fromUserId)
  const id = fromId || accountId || (email ? email.toLowerCase() : '')
  if (!id) return null
  const roles = readWireRoles(p)
  const statusRaw = readWireString(p.status ?? p.Status).toLowerCase()
  const status =
    statusRaw === 'joined' || statusRaw === 'declined' || statusRaw === 'pending'
      ? statusRaw
      : 'pending'
  const nameRaw = readWireString(p.name ?? p.Name)
  const name = nameRaw || email || id
  const out: ScenarioPerson = {
    ...(accountId ? { userId: accountId } : {}),
    id,
    name,
    email,
    status,
    roles,
  }
  if (typeof p.phone === 'string' && p.phone.trim()) out.phone = p.phone.trim()
  if (typeof p.jobTitle === 'string' && p.jobTitle.trim()) out.jobTitle = p.jobTitle.trim()
  if (typeof p.lastName === 'string' && p.lastName.trim()) out.lastName = p.lastName.trim()
  if (p.isDeleted === true) out.isDeleted = true
  if (typeof p.deletionTimeIso === 'string' && p.deletionTimeIso.trim()) {
    out.deletionTimeIso = p.deletionTimeIso.trim()
  }
  if (typeof p.hasBeenInvited === 'boolean') out.hasBeenInvited = p.hasBeenInvited
  if (typeof p.accountInviteSent === 'boolean') out.accountInviteSent = p.accountInviteSent
  if (typeof p.lastInviterId === 'string' && p.lastInviterId.trim()) {
    out.lastInviterId = p.lastInviterId.trim()
  }
  if (typeof p.lastInvitedAtIso === 'string' && p.lastInvitedAtIso.trim()) {
    out.lastInvitedAtIso = p.lastInvitedAtIso.trim()
  }
  return out
}

export function normalizePeople(raw: unknown): ScenarioPerson[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((row) => {
    const person = normalizePerson(row)
    return person ? [person] : []
  })
}

export function personHasRole(person: ScenarioPerson, role: PersonRole): boolean {
  return Array.isArray(person.roles) && person.roles.includes(role)
}

/** Active roster rows that hold the session `owner` seat. */
export function activeOwners(people: readonly ScenarioPerson[]): ScenarioPerson[] {
  return people.filter((p) => personIsActive(p) && personHasRole(p, 'owner'))
}

/**
 * Designated (star) owner userId: prefer session.primaryOwnerUserId when that person
 * still holds owner; else the first active owner by stable userId order.
 */
export function resolveDesignatedOwnerUserId(
  primaryOwnerUserId: string | null | undefined,
  people: readonly ScenarioPerson[],
): string | null {
  const owners = activeOwners(people)
  if (!owners.length) return null
  const preferred = primaryOwnerUserId?.trim()
  if (preferred && owners.some((p) => personUserId(p) === preferred)) return preferred
  const sorted = [...owners].sort((a, b) => personUserId(a).localeCompare(personUserId(b)))
  return personUserId(sorted[0]!)
}

export function isDesignatedOwner(
  person: ScenarioPerson,
  primaryOwnerUserId: string | null | undefined,
  people: readonly ScenarioPerson[],
): boolean {
  if (!personHasRole(person, 'owner') || !personIsActive(person)) return false
  const designated = resolveDesignatedOwnerUserId(primaryOwnerUserId, people)
  return Boolean(designated && personUserId(person) === designated)
}

export function sortRolesByPrivilege(roles: readonly PersonRole[]): PersonRole[] {
  return [...roles].sort(
    (a, b) => PERSON_ROLE_RANK.indexOf(a) - PERSON_ROLE_RANK.indexOf(b),
  )
}

export function formatPersonRoles(person: ScenarioPerson): string {
  return sortRolesByPrivilege(person.roles)
    .map((role) => ROLE_LABEL[role])
    .join(', ')
}

/**
 * Roles shown as a read-only hint when the card has no checkboxes.
 * May include Owner; managers with owner seat also see the Owner checkbox.
 */
export function formatVisibleRosterRoles(person: ScenarioPerson): string {
  return sortRolesByPrivilege(person.roles)
    .map((role) => ROLE_LABEL[role])
    .join(', ')
}

const EDITOR_PERMS = ['scenarios.edit_board', 'buxus.scenarios.editboard'] as const
const PLACE_PERMS = ['scenarios.place_objects', 'buxus.scenarios.placeobjects'] as const
const RUN_PERMS = ['scenarios.run', 'buxus.scenarios.run'] as const
const MANAGE_PERMS = ['scenarios.manage_people', 'buxus.scenarios.managepeople'] as const

function grantedSet(granted: readonly string[] | ReadonlySet<string> | null): Set<string> | null {
  if (!granted) return null
  const out = new Set<string>()
  for (const name of granted) {
    const t = name.trim().toLowerCase()
    if (t) out.add(t)
  }
  return out.size ? out : null
}

function holdsAny(granted: Set<string>, names: readonly string[]): boolean {
  return names.some((name) => granted.has(name))
}

/**
 * Editor needs Edit Board and Place Objects. Trainer needs Run and Manage People.
 * Uses the account’s granted permissions (role configuration), not a role-name list.
 * A role already on the card stays visible so it can be cleared.
 */
export function rosterRoleCanBeOffered(
  role: PersonRole,
  grantedPermissions: readonly string[] | ReadonlySet<string> | null,
  alreadyOn: boolean,
): boolean {
  if (role === 'participant') return true
  // Owner is a session stewardship grant, not an ABP ceiling seat.
  if (role === 'owner') return true
  if (alreadyOn) return true
  const granted = grantedSet(grantedPermissions)
  if (!granted) return false
  if (role === 'editor') return holdsAny(granted, EDITOR_PERMS) && holdsAny(granted, PLACE_PERMS)
  if (role === 'trainer') return holdsAny(granted, RUN_PERMS) && holdsAny(granted, MANAGE_PERMS)
  return false
}

/**
 * Highest *visible* roster role for the list badge (Trainer > Editor > Participant).
 * Owner is seat ownership, not shown as a role chip. Product Admin is Role-override /
 * app entitlement — not a per-scenario person role.
 */
export function personPrivilegeLabel(person: ScenarioPerson): string | null {
  if (personHasRole(person, 'trainer')) return 'Trainer'
  if (personHasRole(person, 'editor')) return 'Editor'
  if (personHasRole(person, 'participant')) return 'Participant'
  return null
}

/**
 * Viewers who prepare the board (Owner / Editor / Trainer) may see elevated-role
 * suffixes on presence toasts. Participants only get the plain "is here" line.
 */
export function viewerSeesPresenceRoleSuffix(viewer: ScenarioPerson | null | undefined): boolean {
  if (!viewer || viewer.isDeleted) return false
  return (
    personHasRole(viewer, 'owner') ||
    personHasRole(viewer, 'editor') ||
    personHasRole(viewer, 'trainer')
  )
}

/**
 * ` as an editor` / ` as a trainer` for preparer toasts only.
 * Never "as an owner" (Owner has no roster checkbox / chip — same as product UI).
 * Owner who is also trainer/editor → that elevated label. Owner-only → plain "is here".
 * Participant (and unset) → empty — never "as a participant".
 */
export function presenceRoleSuffix(
  person: ScenarioPerson,
  viewerSeesRole: boolean,
): string {
  if (!viewerSeesRole) return ''
  if (personHasRole(person, 'trainer')) return ' as a trainer'
  if (personHasRole(person, 'editor')) return ' as an editor'
  return ''
}

/**
 * Join-card host tag after the inviter name.
 * Participant-only stays neutral; Trainer and/or Editor share one short toast-style hint
 * (roles are not differentiated here — never "trainer and editor").
 */
export function joinOfferHostTag(invitee: ScenarioPerson): string {
  const roles = invitee.roles ?? []
  const elevated = roles.some((r) => {
    const key = r.toLowerCase()
    return key === 'trainer' || key === 'editor'
  })
  return elevated ? 'invited you as an editor' : 'invited you'
}

/** True when the roster row has an ABP user id. */
export function personHasStudioAccount(person: Pick<ScenarioPerson, 'userId'>): boolean {
  return studioAccountId(person.userId).length > 0
}

export type StudioInviteNotice = 'invite' | 'resend' | 'reinvite' | 'nudge' | 'register'

/** What the Send button is asking the server to deliver for this person. */
export function inviteNotice(person: ScenarioPerson): StudioInviteNotice {
  if (!personHasStudioAccount(person)) return 'register'
  if (person.status === 'joined') return 'nudge'
  if (person.status === 'declined') return 'reinvite'
  if (person.status === 'pending' && person.hasBeenInvited === false) return 'invite'
  return 'resend'
}

export type InviteActionInfo = {
  label: string
  title: string
  /** True when the action overrides a declined RSVP (✕ already shows on the form). */
  inviteAnyway?: boolean
}

/**
 * Invite / Nudge / Remind / Invite again — labels from roster state.
 * After End (`review`), copy is about belated docs / notes, not RSVP pressure.
 * Email-keyed rows stay Send invite, including a repeat. Nudge is a repeat session invite.
 */
export function inviteAction(
  targets: readonly ScenarioPerson[],
  opts?: { review?: boolean; prepare?: boolean },
): InviteActionInfo {
  if (!targets.length) {
    return { label: 'Invite', title: 'Select from the roster, then send invites' }
  }
  const n = targets.length
  const declined = targets.filter((p) => p.status === 'declined')
  const kinds = new Set(targets.map((p) => inviteNotice(p)))
  const review = Boolean(opts?.review)
  const prepare = Boolean(opts?.prepare)

  if (kinds.size === 1) {
    const k = [...kinds][0]!
    if (k === 'invite' || k === 'register') {
      return {
        label: prepare
          ? n > 1
            ? `Invite to prepare (${n})`
            : 'Invite to prepare'
          : n > 1
            ? `Send invites (${n})`
            : 'Send invite',
        title: review
          ? 'Email the session link, optional docs, and a personal note'
          : prepare
            ? 'Email a sneak-peek invite so they can open while this is still Draft'
            : 'Email their invite with the Join link (and optional docs)',
      }
    }
    if (k === 'resend') {
      if (review) {
        return {
          label: n > 1 ? `Send invite (${n})` : 'Send invite',
          title: 'Send the session link with optional docs or a personal note',
        }
      }
      return {
        label: n > 1 ? `Nudge (${n})` : 'Nudge',
        title: 'Send another invite — they have not responded yet',
      }
    }
    if (k === 'nudge') {
      if (review) {
        return {
          label: n > 1 ? `Send (${n})` : 'Send',
          title: 'Send the session link with optional docs or a personal note',
        }
      }
      return {
        label: n > 1 ? `Remind (${n})` : 'Remind',
        title: 'Send a reminder with the session link (they already accepted)',
      }
    }
    return {
      label: n > 1 ? `Invite again (${n})` : 'Invite again',
      title: review
        ? 'Send the session link again with optional docs or a personal note'
        : 'Send a new invite after they declined (Declined stays visible nearby)',
      inviteAnyway: true,
    }
  }

  return {
    label: n > 1 ? `Send Invites (${n})` : 'Send Invites',
    title: review
      ? 'Sends mail per person (session link, optional docs or note)'
      : declined.length
        ? 'Sends the right mail per person (includes Invite again for declined)'
        : 'Sends the right mail per person (Invite, Nudge, Remind, or Invite again)',
    inviteAnyway: declined.length > 0,
  }
}
